import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

// Polling config — matches Stripe playbook: 5 attempts × 2 seconds
const MAX_ATTEMPTS = 5;
const POLL_INTERVAL_MS = 2000;

export default function CheckoutSuccess() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const legacySid = sp.get("sid");
  const isMock = sp.get("mock") === "1";
  const kind = sp.get("kind");           // "deductible" for excess-fee flow
  const claimId = sp.get("claim_id");    // present on deductible flow

  const [state, setState] = useState({ status: "polling", data: null, message: "" });
  const attempts = useRef(0);

  const pollDeductible = useCallback(async () => {
    if (!claimId) return;
    try {
      const { data } = await api.get(`/claims/${claimId}/deductible/status`);
      if (data.paid) {
        setState({ status: "paid", data: { kind: "deductible", claim_id: claimId }, message: "" });
        return;
      }
      attempts.current += 1;
      if (attempts.current >= MAX_ATTEMPTS) {
        setState({ status: "timeout", data, message: "Still processing. Check your email for confirmation." });
        return;
      }
      setTimeout(pollDeductible, POLL_INTERVAL_MS);
    } catch (e) {
      setState({ status: "error", data: null, message: e?.response?.data?.detail || "Unable to check payment status" });
    }
  }, [claimId]);

  const pollStatus = useCallback(async () => {
    if (!sessionId) return;
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setState({ status: "paid", data, message: "" });
        return;
      }
      if (data.status === "expired") {
        setState({ status: "expired", data, message: "Payment session expired." });
        return;
      }
      attempts.current += 1;
      if (attempts.current >= MAX_ATTEMPTS) {
        setState({ status: "timeout", data, message: "Still processing. Check your email for confirmation." });
        return;
      }
      setTimeout(pollStatus, POLL_INTERVAL_MS);
    } catch (e) {
      setState({ status: "error", data: null, message: e?.response?.data?.detail || "Unable to check payment status" });
    }
  }, [sessionId]);

  const confirmLegacy = useCallback(async () => {
    if (!legacySid) return;
    try {
      const { data } = await api.post(`/subscriptions/${legacySid}/confirm`);
      setState({ status: "paid", data: { subscription_id: legacySid, plan_id: data.plan_id, membership_id: data.membership_id, status: data.status }, message: "" });
    } catch (e) {
      setState({ status: "error", data: null, message: "Could not confirm subscription" });
    }
  }, [legacySid]);

  useEffect(() => {
    if (kind === "deductible" && claimId) pollDeductible();
    else if (sessionId) pollStatus();
    else if (legacySid) confirmLegacy();
  }, [sessionId, legacySid, kind, claimId, pollStatus, pollDeductible, confirmLegacy]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="checkout-success-page">
      <div className="max-w-md text-center px-6">
        {state.status === "polling" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 text-primary flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
            <h1 className="mt-6 font-display text-4xl font-light tracking-tight">Confirming payment…</h1>
            <p className="mt-3 text-muted-foreground">Give us a moment while Stripe confirms your transaction.</p>
          </>
        )}

        {state.status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 text-primary flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h1 className="mt-6 font-display text-4xl font-light tracking-tight">
              {kind === "deductible" ? "Excess fee paid." : (isMock ? "Activated." : "Payment received.")}
            </h1>
            <p className="mt-3 text-muted-foreground" data-testid="checkout-success-message">
              {kind === "deductible"
                ? "Thanks — your claim will now proceed. You'll get an update as soon as the repair starts."
                : "Your Treasurescare plan is active. You'll receive a confirmation email shortly."}
            </p>
            <Button
              onClick={() => nav(kind === "deductible" && claimId ? `/app/claims/${claimId}` : "/app")}
              className="mt-6"
              data-testid="checkout-success-cta"
            >
              {kind === "deductible" ? "Back to claim" : "Go to dashboard"}
            </Button>
          </>
        )}

        {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-destructive/10 text-destructive flex items-center justify-center">
              <XCircle className="w-8 h-8" />
            </div>
            <h1 className="mt-6 font-display text-4xl font-light tracking-tight">
              {state.status === "expired" ? "Session expired" : state.status === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="mt-3 text-muted-foreground">{state.message}</p>
            <div className="mt-6 flex gap-2 justify-center">
              <Button variant="outline" onClick={() => nav("/app/subscription")}>Try again</Button>
              <Button onClick={() => nav("/app")}>Dashboard</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
